import useAuthTeam from '@apps/www/src/www/hooks/useAuthTeam';
import useAuthUser from '@apps/www/src/www/hooks/useAuthUser';
import useFeatureFlag from '@apps/www/src/www/hooks/useFeatureFlag';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import SearchFilter from '@pkgs/shared/enums/SearchFilter';
import parseQueryParam from '@pkgs/shared/helpers/parseQueryParam';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { usePrevious } from 'react-use';

export default function useSearchFilter() {
	const router = useRouter();
	const authUser = useAuthUser(['username']);
	const hasSearchFilter = useFeatureFlag('search_filter');
	const team = useAuthTeam();
	const routerAsPath = router.asPath;
	const previousRouterAsPath = usePrevious(routerAsPath);
	const isOnSearchPage = routerAsPath.startsWith('/search/') && router.query.image === undefined;
	const [filterState, setFilterState] = useState<ValueOf<typeof SearchFilter> | null>(null);
	let showFilter = isOnSearchPage;

	let filter: ValueOf<typeof SearchFilter> = SearchFilter.PUBLIC;

	// Only if user has feature flag
	if (hasSearchFilter) {
		const filterQueryParam = parseQueryParam(router.query.filter);

		if (!filterQueryParam) {
			// If on a team route, default to team filter
			if (
				team &&
				((team.teamFeedAsHomepage && routerAsPath === '/') ||
					(!team.teamFeedAsHomepage && routerAsPath.startsWith('/team/')))
			) {
				filter = SearchFilter.TEAM;
				showFilter = true;
			}
			// If on a user route, default to user filter
			else if (authUser && routerAsPath.startsWith(`/${authUser.username}/`)) {
				filter = SearchFilter.USER;
				showFilter = true;
			} else {
				filter = SearchFilter.PUBLIC;
			}
		} else {
			filter = Object.values(SearchFilter).includes(filterQueryParam)
				? (filterQueryParam as ValueOf<typeof SearchFilter>)
				: SearchFilter.PUBLIC;
		}

		if (filterState !== null) {
			filter = filterState;
		}

		// If filter is team but user is not part of a team, default to public
		if (filter === SearchFilter.TEAM && !team) {
			filter = SearchFilter.PUBLIC;
		}
	}

	const setFilter = useEventCallback((filter: ValueOf<typeof SearchFilter>) => {
		if (isOnSearchPage) {
			router.replace({
				pathname: router.pathname,
				query: {
					...router.query,
					filter,
				},
			});
		} else {
			setFilterState(filter);
		}
	});

	// Clear filter state when navigating
	useEffect(() => {
		if (routerAsPath !== previousRouterAsPath) {
			setFilterState(null);
		}
	}, [routerAsPath, previousRouterAsPath]);

	return [filter, setFilter, hasSearchFilter && showFilter] as const;
}
